import React from "react";
import "./App.css";
import { ConfirmProvider } from "material-ui-confirm";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material";

import { RecoilRoot } from "recoil";
import RecoilNexus from "recoil-nexus";

import mainTheme from "./themes/MainTheme";
import NewColors from "./themes/NewColors";
import { SafetyWrapper } from "./components/organisms/SafetyWrappers";

import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";

import LandingPage from "./pages/landing/LandingPage";
import SignInPage from "./pages/signin/SignInPage";
import HomePage from "./pages/home/HomePage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import ConnectCodePage from "./pages/onboarding/ConnectCodePage";
import "./i18n";
// import TrainingPlan from "./pages/home/plan/TrainingPlan";
// import TrainingPlan from "./pages/home/plan/TrainingPlanV2";
import TrainingPlan from "./pages/home/plan/TrainingPlanV3";
import PrintableTrainingPlan from "./pages/home/plan/PrintableTrainingPlan";
import ClientMealPlanViewer from "./pages/client-meal-plan/ClientMealPlanViewer";
import ClientRecipeViewer from "./pages/client-recipe/ClientRecipeViewer";
import SubscribePage from "./pages/home/subscribe/SubscribePage";
import useWindowSize from "./hooks/useWindowSize";
import { initCoachConsoleDocumentIdsFromUrl } from "./utils/coachConsoleDocumentIds";
import consola from "consola";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - dashboard data doesn't change frequently
      cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 3, // Retry failed requests 3 times
    },
  },
});

const organizationBranding = {
  // Organization 1 - Purple theme (default)
  "org-1": {
    primary: NewColors.charts.purple, // Deep purple
    primaryLight: NewColors.sectionBackgroundTinted.purple, // Light purple
    secondary: NewColors.charts.orange, // Deep orange
    secondaryLight: NewColors.nutrition.proteinsBackground, // Light orange
  },
  // Organization 2 - Blue theme
  "org-2": {
    primary: NewColors.charts.teal, // Deep blue
    primaryLight: NewColors.sectionBackgroundTinted.blue, // Light blue
    secondary: NewColors.charts.green, // Green
    secondaryLight: NewColors.sectionBackgroundTinted.green, // Light green
  },
  // Organization 3 - Red theme
  "org-3": {
    primary: NewColors.status.error, // Deep red
    primaryLight: NewColors.nutrition.proteinsBackground, // Light red
    secondary: NewColors.charts.blue, // Blue
    secondaryLight: NewColors.sectionBackgroundTinted.blue, // Light blue
  },
};

// Configure logging levels based on environment
// eslint-disable-next-line no-undef
if (process.env.NODE_ENV === "production") {
  consola.level = 2; // Only show warnings and errors in production
} else {
  // Development:
  // 0=silent, 1=error, 2=warn, 3=info, 4=debug, 5=trace, 6=verbose
  consola.level = 3; // Show info, warnings, and errors (but not debug noise)
}

Bugsnag.start({
  apiKey: "f2537d0a9e8bf06a884c8b33a509a99a",
  plugins: [new BugsnagPluginReact()],
  appVersion: "1.0.0",
});

const onError = (event) => {
  consola.error(new Error("Error captured by Bugsnag: ", event));
};

const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);

function App() {
  useWindowSize();
  React.useEffect(() => {
    initCoachConsoleDocumentIdsFromUrl();
  }, []);
  React.useEffect(() => {
    if (!("Notification" in window)) {
      //
    } else {
      Notification.requestPermission((permission) => {
        if (permission === "granted") {
          //
        } else {
          //
        }
      });
    }
  }, []);

  return (
    <ErrorBoundary onError={onError}>
      <QueryClientProvider client={queryClient}>
        <RecoilRoot>
          <RecoilNexus />
          <ThemeProvider theme={mainTheme}>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <ConfirmProvider>
                <SafetyWrapper
                  loadingFallback={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                        fontSize: "18px",
                        fontFamily: "sans-serif",
                      }}
                    >
                      Loading application...
                    </div>
                  }
                >
                  <Routes>
                    <Route path={"/"} element={<LandingPage />} />
                    <Route path={"/signin"} element={<SignInPage />} />
                    <Route path={"/home/*"} element={<HomePage />} />
                    <Route path={"/onboarding"} element={<OnboardingPage />} />
                    <Route path={"/connect"} element={<ConnectCodePage />} />
                    <Route
                      path={"/plan/:organizationId/:id"}
                      element={
                        <TrainingPlan
                          brandColors={organizationBranding["org-2"]}
                        />
                      }
                    />
                    <Route
                      path={"/printable-plan/:organizationId/:id"}
                      element={<PrintableTrainingPlan />}
                    />
                    <Route
                      path={"/meal-plan/:organizationId/:mealPlanId"}
                      element={<ClientMealPlanViewer />}
                    />
                    <Route
                      path={"/recipe/:organizationId/:recipeId"}
                      element={<ClientRecipeViewer />}
                    />
                    <Route path={"/subscribe"} element={<SubscribePage />} />
                  </Routes>
                </SafetyWrapper>
              </ConfirmProvider>
            </Router>
          </ThemeProvider>
        </RecoilRoot>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
